// ── EML import qualification logic ───────────────────────────────────────────
// Pure helpers that decide, from the AI analysis of an ENERGA tender, whether
// GK ELEKTIM is qualified to take it on (RKW work authorizations + branch
// agreements). Kept separate from EmlImportPage so the rules can be unit-tested
// and reused by both the per-tender badge and the "Dodaj wszystkie z
// uprawnieniami" bulk action without drifting apart.
import type { EmlRfxResult, ZECapabilities } from "./types";

type Signals = EmlRfxResult["signals"];

/** Zakresy poza portfelem ZE: sama robota budowlana i projekt+wykonawstwo. Backend
 *  odrzuca takie przetargi przy dodawaniu i ukrywa je na liście /ze-tenders — tu służą
 *  do oznaczenia wiersza, zanim ktoś kliknie „Dodaj". */
const CONSTRUCTION_SCOPES = ["wykonawstwo", "projekt_i_wykonawstwo"];

/**
 * Zakres wiersza importu w tej samej kolejności pierwszeństwa, której używa backend
 * (`compute_ze_warnings`, `design_scope_only`): prefiks nazwy ("P+W …") decyduje sam,
 * a sygnał AI dostaje głos dopiero, gdy nazwa nic nie mówi. "" = nieustalony.
 */
export function effectiveScope(res: EmlRfxResult | undefined): string {
  return res?.work_scope || res?.signals?.contract_scope || "";
}

/** Czy zakres wiersza obejmuje roboty budowlane (W albo P+W). */
export function isConstructionScope(res: EmlRfxResult | undefined): boolean {
  return CONSTRUCTION_SCOPES.includes(effectiveScope(res));
}

/**
 * Mirrors the backend `compute_ze_warnings`. Returns a list of human-readable
 * warnings (Polish) describing every missing qualification or branch agreement
 * for the given tender. An empty list means GK ELEKTIM is fully cleared.
 *
 * Returns [] when capabilities or signals are missing — callers that need to
 * distinguish "verified OK" from "cannot verify" must check `caps`/`sig`
 * themselves (see {@link isQualified}).
 */
export function computeWarnings(
  sig: Signals,
  oddzial: string | null,
  caps: ZECapabilities | undefined,
): string[] {
  if (!caps || !sig) return [];
  const scope = sig.contract_scope ?? "";
  const rkw = sig.rkw_required ?? [];
  const isProj = scope === "projekt" || scope === "projekt_i_wykonawstwo";
  const isBud = scope === "wykonawstwo" || scope === "projekt_i_wykonawstwo";
  const warnings: string[] = [];
  if (isProj) {
    const projSet = new Set(caps.proj_rkw);
    rkw.forEach((r) => {
      if (!projSet.has(r)) warnings.push(`Brak uprawnień projektowych: ${r}`);
    });
  }
  if (isBud) {
    const budSet = new Set(caps.bud_rkw);
    rkw.forEach((r) => {
      if (!budSet.has(r)) warnings.push(`Brak uprawnień wykonawczych: ${r}`);
    });
    if (!caps.porozumienia_oddzialy.length) {
      warnings.push(
        "Brak porozumień z oddziałami ENERGA — roboty budowlane niedostępne",
      );
    } else if (oddzial) {
      const norm = oddzial.toLowerCase();
      if (
        !caps.porozumienia_oddzialy.some(
          (o) =>
            norm.includes(o.toLowerCase()) || o.toLowerCase().includes(norm),
        )
      ) {
        warnings.push(`Brak porozumienia z oddziałem: ${oddzial}`);
      }
    }
  }
  return warnings;
}

/**
 * Whether a tender qualifies for the "Dodaj wszystkie z uprawnieniami" bulk
 * action: the analysis must have detected at least one required RKW and there
 * must be no missing qualifications/agreements. When capabilities haven't
 * loaded we cannot verify anything, so we conservatively return false rather
 * than treating an unverified tender as qualified.
 */
export function isQualified(
  sig: Signals,
  oddzial: string | null,
  caps: ZECapabilities | undefined,
): boolean {
  if (!caps) return false;
  if (!sig?.rkw_required?.length) return false;
  return computeWarnings(sig, oddzial, caps).length === 0;
}

/**
 * Summary label for the RKW badge. Green "Uprawnienia OK" only when there are
 * no warnings at all (matching {@link isQualified}); otherwise an amber summary
 * naming the kinds of gaps. Agreement-only gaps render as their own phrase so
 * the label never reads the nonsensical "Brak 0 uprawnień".
 */
export function rkwBadgeLabel(warnings: string[]): string {
  if (warnings.length === 0) return "✓ Uprawnienia OK";
  const rkwGaps = warnings.filter(
    (w) => w.includes("projektowych") || w.includes("wykonawczych"),
  ).length;
  const agreementMissing = warnings.some((w) => w.includes("porozumie"));
  const parts = [
    rkwGaps > 0 ? `Brak ${rkwGaps} uprawnień` : null,
    agreementMissing ? "brak porozumienia z oddziałem" : null,
  ].filter(Boolean);
  return "⚠ " + parts.join(", ");
}
